import clsx from "clsx";
import { format } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import TagManager from "react-gtm-module";

import Button from "../button";
import Input from "../input";
import Modal from "../modal";
import Select from "../select";
import Textarea from "../textarea";
import DateSelect from "../date-select";
import DateTimeSelect from "../date-time-select";
import ConfirmationModal from "../confirmation-modal";
import { activeCampaignSubmit } from "../../lib/forms";
import { EVENT_BUDGET_CHOICES, EVENT_TYPE_CHOICES } from "../../consts";

const formatDate = (day) => {
  return format(day, "yyyy-MM-dd");
};

// Active Campaign field names.
const fieldMap = {
  companyName: "field[68]",
  bookingDate: "field[11]",
  bookingTime: "field[12]",
  location: "field[62]",
  attendees: "field[13]",
  eventType: "field[113]",
  budget: "field[111]",
  details: "field[14]",
  subscribe: "field[24][]",
};

const EventFormModal = ({ locations, ...props }) => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { register, formState, handleSubmit, watch, getValues, setValue } =
    useForm({
      mode: "onChange",
      // Default Active Campaign values for this
      // specific form.
      defaultValues: {
        u: "4",
        f: "4",
        s: "0",
        c: "0",
        m: "0",
        act: "sub",
        v: "2",
        [fieldMap.bookingDate]: "",
      },
    });

  watch();

  const onSubmit = (data) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "contact_us",
        type: "event",
        ...data,
      },
    });

    activeCampaignSubmit(data, false)
      .then(() => setSubmitted(true))
      .catch((e) => setError(e));
  };

  const { errors } = formState;

  const bookingDateField = fieldMap.bookingDate.split("[")[1].slice(0, -1);

  const getError = (field) => {
    const fieldNum = field.split("[")[1].slice(0, -1);

    if (errors.field && errors.field[fieldNum]) {
      return errors.field[fieldNum].message;
    }

    return null;
  };

  if (submitted) {
    return <ConfirmationModal {...props} />;
  }

  if (error) {
    return (
      <Modal {...props}>
        <>
          <h2 className="font-bold text-2xl text-center">
            Something went wrong!
          </h2>

          <p className="text-md my-4 text-center font-light">
            {error?.message}
          </p>
        </>
      </Modal>
    );
  }

  return (
    <Modal {...props}>
      <form className={clsx("p-6")} onSubmit={handleSubmit(onSubmit)}>
        <h2 className="font-bold text-3xl text-center">
          Interested in hosting an event?
        </h2>

        <div className="my-4">
          <div className="form-group-inline">
            <Input
              type="text"
              placeholder="First name *"
              {...register("firstname", {
                required: "First name is required",
              })}
              errorText={errors.firstname && errors.firstname.message}
              inline
            />
            <Input
              type="text"
              placeholder="Last name *"
              {...register("lastname", { required: "Last name is required" })}
              errorText={errors.lastname && errors.lastname.message}
              inline
            />
          </div>

          <Input
            type="text"
            placeholder="Work email address *"
            {...register("email", { required: "Email is required" })}
            errorText={errors.email && errors.email.message}
          />

          <Input
            type="text"
            placeholder="Phone number *"
            {...register("phone", {
              required: "Phone number is required",
            })}
            errorText={errors.phone && errors.phone.message}
          />
          <Input
            type="text"
            placeholder="Company name *"
            {...register(fieldMap.companyName, {
              required: "Company name is required",
            })}
            errorText={getError(fieldMap.companyName)}
          />

          <div className="form-group-inline">
            <DateSelect
              {...register(fieldMap.bookingDate, {
                required: "Booking date is required",
              })}
              placeholder="Desired Booking Date *"
              selectedDate={getValues().field?.[bookingDateField]}
              disabledDates={(day) => day < new Date()}
              onChange={(day) => {
                setValue(fieldMap.bookingDate, formatDate(day));
              }}
              errorText={getError(fieldMap.bookingDate)}
              inline
            />

            <DateTimeSelect
              {...register(fieldMap.bookingTime, {
                required: "Booking time is required",
              })}
              date={getValues().field?.[bookingDateField]}
              type="text"
              placeholder="Desired Booking Time *"
              onChange={(e) => {
                setValue(fieldMap.bookingTime, e.target.value);
              }}
              errorText={getError(fieldMap.bookingTime)}
              inline
            />
          </div>

          <Select
            {...register(fieldMap.location, {
              required: "Location is required",
            })}
            errorText={getError(fieldMap.location)}
          >
            <option value="" disabled selected>
              Select your location of interest *
            </option>
            {locations.map((location) => (
              <option
                value={`${location.address1}, ${location.city} ${location.province}`}
                key={location.slug}
              >
                {location.address1}, {location.city} {location.province}
              </option>
            ))}
          </Select>

          <Select
            {...register(fieldMap.eventType, {
              required: "Type of Event is required",
            })}
            errorText={getError(fieldMap.eventType)}
          >
            <option value="" disabled selected>
              Type of Event *
            </option>
            {EVENT_TYPE_CHOICES.map((choice) => (
              <option value={choice} key={choice}>
                {choice}
              </option>
            ))}
          </Select>

          <Select
            {...register(fieldMap.budget, {
              required: "Event Budget is required",
            })}
            errorText={getError(fieldMap.budget)}
          >
            <option value="" disabled selected>
              Event Budget *
            </option>
            {EVENT_BUDGET_CHOICES.map((choice) => (
              <option value={choice} key={choice}>
                {choice}
              </option>
            ))}
          </Select>

          <Input
            type="text"
            placeholder="Amount of attendees *"
            {...register(fieldMap.attendees, {
              required: "Amount of attendees is required",
            })}
            errorText={getError(fieldMap.attendees)}
          />

          <Textarea
            type="text"
            placeholder="Event description and any additional information"
            rows="3"
            {...register(fieldMap.details)}
          />
        </div>

        <Button type="submit" className="w-full my-4" outline>
          Submit
        </Button>
      </form>
    </Modal>
  );
};

export default EventFormModal;
